// The quote flow's analytics scope: every `quote_flow_*` event carries
// the same base props (which quote, which product, where the flow's
// chrome sits, how this device got in) without every call site having to
// thread them through by hand. Sits on top of the app-level `track()`
// fan-out (AnalyticsContext) — this module never talks to PostHog or
// signals directly.

import {
  createContext,
  useContext,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from "react";

import {
  useAnalytics,
  type AnalyticsProperties,
} from "analytics/AnalyticsContext";
import { NetworkError } from "hooks/usePublicAPI";

import { createQuoteFlowTracker } from "./analytics-tracker";
import { reportQuoteFlowFailure } from "./reporting";

/** What the quote is for — a pre-need policy today, an at-need case later.
 * A property on every event, never baked into an event name. */
export type QuoteType = "policy" | "case";

/** Where the flow's chrome currently sits — the URL's station vocabulary.
 * Defined here rather than imported from QuoteFlow.tsx (which would be
 * circular — that module imports this name instead of declaring its own
 * `FlowStep`). `review` is a station of its own: the flow's URL names it,
 * even though the server's derived step never says it. */
export type QuoteFlowStepName =
  | "quiz"
  | "schedule-call"
  | "quote"
  | "application"
  | "review"
  | "payment"
  | "sign"
  | "finalize"
  | "done"
  | "account";

/** Which channel this device's credential arrived through. */
export type QuoteFlowCredential = "link" | "signature" | "none";

/**
 * A provenance value the flow has not learned yet: nothing has come back,
 * so the event reports the value as unknown rather than claiming the
 * absence of one. A response that answers "there is nothing to name"
 * reports PROVENANCE_NONE instead, the way `credential` already does.
 * Both are real strings, so an equality filter can match them.
 */
const PROVENANCE_UNKNOWN = "unknown";
export const PROVENANCE_NONE = "none";

/** Where this visit came from, as the flow's fetches learn it. Null on
 * either field means no response has said yet. */
export interface QuoteFlowProvenance {
  /** Which channel created the quote on screen. */
  quoteOrigin: string | null;
  /** The door this visitor came in through — the channel of the link
   * their session was redeemed from. */
  grantChannel: string | null;
  /** Who asked for the credential this visitor holds, which is not
   * always whoever created the quote. */
  grantOrigin: string | null;
}

interface QuoteFlowAnalyticsScope {
  quoteId: string;
  quoteType: QuoteType;
  credential: QuoteFlowCredential;
}

interface QuoteFlowAnalyticsContextType {
  track(event: string, properties?: AnalyticsProperties): void;
  /**
   * Reports an event under `step` and names it as the step for every
   * event from here on. For a face that renders before the URL moves to
   * its station: the flow's `setStep` only learns the new station after
   * the passive effect reporting the face has already run.
   */
  trackAtStep(
    step: QuoteFlowStepName,
    event: string,
    properties?: AnalyticsProperties,
  ): void;
  /**
   * Names the step every event from here on is stamped with. Called from
   * a LAYOUT effect by the flow that derives it, so the passive effects
   * reporting a screen in the same commit already stamp the new step —
   * a plain effect would run after them and stamp the previous one.
   */
  setStep(step: QuoteFlowStepName | null): void;
  /**
   * Records where this visit came from, for every event from here on.
   * Only the flow's own fetches learn it, so it arrives through the
   * context rather than as a prop.
   *
   * Call it where a response lands, beside the state it sets, NOT from an
   * effect: React runs a mounting child's effects before its parent's,
   * and children of this flow report events as they arrive, so an effect
   * would lose the race to them.
   */
  noteProvenance(provenance: QuoteFlowProvenance): void;
  /** Reports an unexpected failure (Sentry, tagged with quote_id +
   * operation) and always emits `quote_flow_request_failed` so the
   * failure funnel is visible without reading Sentry. Call only after
   * the branches that handle expected statuses (404 polling, 409
   * refusals, 402 declines) have already returned. */
  reportFailure(operation: string, error: unknown): void;
}

const QuoteFlowAnalyticsContext = createContext<
  QuoteFlowAnalyticsContextType | undefined
>(undefined);

type QuoteFlowAnalyticsProviderProps = QuoteFlowAnalyticsScope & {
  children: ReactNode;
};

export const QuoteFlowAnalyticsProvider: FC<
  QuoteFlowAnalyticsProviderProps
> = ({ quoteId, quoteType, credential, children }) => {
  const { track: trackAppEvent } = useAnalytics();

  // A ref (rather than useCallback deps) so `track`/`reportFailure` stay
  // referentially stable across renders while always stamping the
  // latest scope.
  const scopeRef = useRef<QuoteFlowAnalyticsScope>({
    quoteId,
    quoteType,
    credential,
  });
  scopeRef.current = { quoteId, quoteType, credential };

  // Not part of the scope above because it does not come from a prop —
  // see `noteProvenance`.
  const provenanceRef = useRef<QuoteFlowProvenance>({
    quoteOrigin: null,
    grantChannel: null,
    grantOrigin: null,
  });

  // The app-level `track` changes identity when PostHog or the signal
  // client finishes loading. The tracker (and the step it holds) must
  // outlive that, so it is built once and sends through the latest one.
  const sendRef = useRef(trackAppEvent);
  sendRef.current = trackAppEvent;

  const [value] = useState<QuoteFlowAnalyticsContextType>(() => {
    const { track, setStep, trackAtStep } =
      createQuoteFlowTracker<QuoteFlowStepName>({
        send: (event, properties) => sendRef.current(event, properties),
        stamp: () => {
          const scope = scopeRef.current;
          const { quoteOrigin, grantChannel, grantOrigin } =
            provenanceRef.current;
          return {
            quote_id: scope.quoteId,
            quote_type: scope.quoteType,
            credential: scope.credential,
            quote_origin: quoteOrigin ?? PROVENANCE_UNKNOWN,
            grant_channel: grantChannel ?? PROVENANCE_UNKNOWN,
            grant_origin: grantOrigin ?? PROVENANCE_UNKNOWN,
          };
        },
      });

    const reportFailure = (operation: string, error: unknown) => {
      const status = error instanceof NetworkError ? error.statusCode : null;
      track("quote_flow_request_failed", { operation, status });
      reportQuoteFlowFailure(error, {
        operation,
        quoteId: scopeRef.current.quoteId,
      });
    };

    const noteProvenance = (provenance: QuoteFlowProvenance) => {
      provenanceRef.current = provenance;
    };

    return { track, trackAtStep, setStep, reportFailure, noteProvenance };
  });

  return (
    <QuoteFlowAnalyticsContext.Provider value={value}>
      {children}
    </QuoteFlowAnalyticsContext.Provider>
  );
};

export function useQuoteFlowAnalytics(): QuoteFlowAnalyticsContextType {
  const context = useContext(QuoteFlowAnalyticsContext);
  if (context === undefined) {
    throw new Error(
      "useQuoteFlowAnalytics must be used within a QuoteFlowAnalyticsProvider",
    );
  }
  return context;
}
